// The builder (dev.codedesign.ai) and the accounts app find the logged-in user
// through the `token` and `userId` cookies that the accounts app sets on the
// shared base domain at login. The dashboard copies them into its own storage
// once and never needs them again, so it stays logged in after they are gone:
// Safari deletes cookies set from JavaScript after 7 days, other browsers after
// the 30 days they are set for, and the dashboard's own login form never sets
// them at all. Opening the builder then finds no login and bounces the user
// straight back to the dashboard (Sep 2026: a customer on Safari, 8 days after
// logging in, could not open the editor at all).
//
// sharedAuthCookieWrites() returns the cookies to write so the dashboard's
// session is visible to the builder again. It only fills in missing cookies and
// never replaces one that exists: the accounts app owns them, and a different
// value means a newer login. Pure logic, no browser dependencies.

// Same lifetime the accounts app uses (Safari still caps it at 7 days).
export const AUTH_COOKIE_MAX_AGE = 30 * 24 * 60 * 60

const SHARED_BASE_DOMAINS = ['codedesign.ai', 'codedesign.app', 'cdsn.me']

// JWTs and Mongo ids only; anything else is not written into a cookie.
const SAFE_VALUE = /^[A-Za-z0-9._-]+$/

/** ".codedesign.ai" for any host on a shared base domain, otherwise null. */
export function sharedCookieDomain(hostname) {
    const host = String(hostname || '').toLowerCase()
    const base = SHARED_BASE_DOMAINS.find((d) => host === d || host.endsWith(`.${d}`))
    return base ? `.${base}` : null
}

export function readCookie(cookieString, name) {
    for (const part of String(cookieString || '').split(';')) {
        const [key, ...rest] = part.trim().split('=')
        if (key === name) return rest.join('=')
    }
    return null
}

/** Cookie strings to assign to document.cookie (empty when nothing is missing). */
export function sharedAuthCookieWrites({ cookieString, hostname, token, userId, maxAge = AUTH_COOKIE_MAX_AGE }) {
    const domain = sharedCookieDomain(hostname)
    if (!domain || !token || !userId) return []
    if (!SAFE_VALUE.test(token) || !SAFE_VALUE.test(userId)) return []
    if (readCookie(cookieString, 'token')) return []
    const attributes = `Domain=${domain}; Path=/; Max-Age=${maxAge}; Secure; SameSite=Lax`
    return [`token=${token}; ${attributes}`, `userId=${userId}; ${attributes}`]
}
